import type { ScheduleStatus } from "model/Schedule";
import { organizationalStructure as organizationalStructureService } from "services/api/company/organizationalStructure.service";
import { schedule as scheduleService } from "services/api/company/schedule.service";
import type { ResolutionDetails } from "services/api/company/schedule.service";
import { worker as workerService } from "services/api/company/worker.service";
import { err, ok } from "utils/result";
import type { Result } from "utils/result";
import type { ShiftDetails } from "web-apps/company/components/schedule/components/common/modals/shiftDetailsModal.component";
import type {
  CompanyWorker,
  LoadCompanyWorkersError,
  LoadOrganizationalStructureError,
  LoadShiftDetailsError,
  OrganizationalStructureListItem,
  RefreshShiftStatusError,
} from "./schedule.utils.types";
import { resolveScheduleShiftStatus } from "./schedule.status";

interface RefreshedShiftStatus {
  readonly details: ResolutionDetails[];
  readonly status: ScheduleStatus;
}

/**
 * Loads company workers and returns a Result with parsed data.
 */
export const loadCompanyWorkers = async (
  companyId: number,
): Promise<Result<CompanyWorker[], LoadCompanyWorkersError>> => {
  try {
    const response = await workerService.getCompanyWorkers(companyId, false);
    return ok(response.data);
  } catch {
    return err("request-failed");
  }
};

/**
 * Loads organizational structure list and returns a Result with parsed data.
 */
export const loadOrganizationalStructureList = async (
  companyId: number,
): Promise<
  Result<OrganizationalStructureListItem[], LoadOrganizationalStructureError>
> => {
  try {
    const response =
      await organizationalStructureService.getOrganizationalStructureList(
        companyId,
      );
    return ok(response.data);
  } catch {
    return err("request-failed");
  }
};

/**
 * Loads shift details for a specific gig shift.
 */
export const loadShiftDetails = async (
  companyId: number,
  gigId: number,
  gigShiftId: number,
): Promise<Result<ShiftDetails | null, LoadShiftDetailsError>> => {
  try {
    const response = await scheduleService.getShiftDetails(
      companyId,
      gigId,
      gigShiftId,
    );
    return ok(response.data ?? null);
  } catch {
    return err("request-failed");
  }
};

/**
 * Loads and parses shift status for the current shift.
 */
export const loadShiftStatus = async (
  companyId: number,
  gigShiftId: number,
  workerId: number,
): Promise<Result<RefreshedShiftStatus, RefreshShiftStatusError>> => {
  try {
    const response = await scheduleService.getShiftStatus(
      companyId,
      gigShiftId,
      workerId,
    );
    return ok({
      details: response.data.details,
      status: resolveScheduleShiftStatus({
        gigType: response.data.gigType,
        status: response.data.status,
      }),
    });
  } catch {
    return err("request-failed");
  }
};
